import { useState } from "react";
import { useNotifications } from "@/hooks/useNotifications";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useQuery } from "@tanstack/react-query";
import { PlanFormHeader } from "./PlanFormHeader";
import { PlanNameInput } from "./PlanNameInput";
import { PlanDescriptionInput } from "./PlanDescriptionInput";
import { RoutineSelector } from "./RoutineSelector";

interface PlanFormContainerProps {
  onPlanAdded: () => void;
}

export const PlanFormContainer = ({ onPlanAdded }: PlanFormContainerProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedRoutines, setSelectedRoutines] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { success, error: showError } = useNotifications();
  const { profile } = useProfile();

  const { data: availableRoutines } = useQuery({
    queryKey: ["available-routines"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("routines")
        .select("*")
        .eq("user_id", user.id);

      if (error) throw error;
      return data;
    },
  });

  const handleCreatePlan = async () => {
    if (!name.trim()) {
      showError("El nombre del plan es requerido");
      return;
    }

    setIsLoading(true);
    try {
      const { data: planData, error: planError } = await supabase
        .from("workout_plans")
        .insert([{
          name,
          description: description || null,
          created_by: profile.id,
          is_personal: profile.role !== "profesor" && profile.role !== "manager"
        }])
        .select()
        .single();

      if (planError) throw planError;

      if (selectedRoutines.length > 0) {
        const routineInserts = selectedRoutines.map(routineId => ({
          workout_plan_id: planData.id,
          routine_id: routineId,
        }));

        const { error: routinesError } = await supabase
          .from("workout_plan_routines")
          .insert(routineInserts);

        if (routinesError) throw routinesError;
      }

      success("Plan creado", {
        description: "El plan de entrenamiento ha sido creado correctamente"
      });
      
      onPlanAdded();
      setName("");
      setDescription("");
      setSelectedRoutines([]);
    } catch (error) {
      console.error("Error creating workout plan:", error);
      showError("No se pudo crear el plan de entrenamiento");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 mb-8">
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <PlanNameInput name={name} onChange={setName} />
          <PlanFormHeader onCreatePlan={handleCreatePlan} isLoading={isLoading} />
        </div>
        
        <PlanDescriptionInput description={description} onChange={setDescription} />

        <RoutineSelector
          availableRoutines={availableRoutines || []}
          selectedRoutines={selectedRoutines}
          onRoutineToggle={(routineId) => {
            setSelectedRoutines(prev => 
              prev.includes(routineId)
                ? prev.filter(id => id !== routineId)
                : [...prev, routineId]
            );
          }}
        />
      </div>
    </div>
  );
};